import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { AuthContext, type AuthContextValue, type AuthUser } from '@/features/auth/auth-context';
import { getJson, postJson } from '@/lib/apiClient';
import { tokenStore } from '@/features/auth/tokenStore';

interface LoginResponse {
  token: string;
  user: AuthUser;
}

/**
 * Token-based auth provider. On mount it restores the session from a stored
 * token (calling /auth/me); if the token is invalid it is cleared. The axios
 * interceptor reads the same token, so authenticated calls just work.
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(() => tokenStore.get() !== null);

  // Restore the session once on load.
  useEffect(() => {
    if (tokenStore.get() === null) {
      return;
    }
    let active = true;
    getJson<{ user: AuthUser }>('/auth/me')
      .then((res) => {
        if (active) {
          setUser(res.user);
        }
      })
      .catch(() => {
        tokenStore.clear();
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { token, user: authUser } = await postJson<LoginResponse>('/auth/login', {
      email,
      password,
    });
    tokenStore.set(token);
    setUser(authUser);
  }, []);

  const logout = useCallback(() => {
    // Best-effort server-side revoke; clear locally regardless.
    void postJson('/auth/logout').catch(() => undefined);
    tokenStore.clear();
    setUser(null);
  }, []);

  const updateProfile = useCallback((data: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      return { ...prev, ...data };
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: user !== null, isLoading, login, logout, updateProfile }),
    [user, isLoading, login, logout, updateProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
